// ./src/components/features/Forms/FormsEditModal/FormsEditModal.tsx
import React, { useState, useEffect } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  ActionIcon,
  Tooltip,
  Accordion,
  Switch,
  NumberInput,
  Group,
  Text,
  TagsInput,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { IconCopy, IconCheck } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Form, FormInput, TEMPLATE_TYPES } from "@/utils/api";
import { AppDrawer, StatusSelector } from "@/components/generic";
import { AccountEmailSelector } from "@/components/features/AccountEmails/AccountEmailSelector";
import { ConnectorSelector } from "@/components/features/Connectors/ConnectorSelector";
import { EmailTemplateSelector } from "@/components/features/EmailTemplates";
import { GibberishCheckSelector } from "../GibberishCheckSelector";
import {
  IpFilterEditor,
  IpFilterValue,
} from "@/components/features/Forms/IpFilterEditor";
import {
  CONNECTOR_TYPES,
  FILE_STORAGE_CONNECTOR_TYPES,
  FEATURE_CODES,
  OPERATION_RIGHTS,
} from "@/config";
import { parseOptions } from "@/utils";
import { useApp } from "@/contexts";
import { usePremiumField, useTimeFormat } from "@/hooks";
import { WorkflowSelector } from "../../Workflows";

// Enable UTC plugin for dayjs
dayjs.extend(utc);

const parseReferers = (str: string): string[] =>
  str
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const serializeReferers = (arr: string[]): string => arr.join(",");

/**
 * Parse IP filter JSON from server into form state
 */
const parseIpFilterForForm = (
  ipFilterJson: string | null | undefined,
): {
  ipFilterMode: "allow" | "deny";
  ipFilterAddresses: string[];
  ipFilterMessage: string;
} => {
  const defaultConfig = {
    mode: "deny" as const,
    addresses: [] as string[],
    message: "",
  };

  const config = parseOptions(ipFilterJson ?? undefined, defaultConfig);

  return {
    ipFilterMode: config.mode,
    ipFilterAddresses: Array.isArray(config.addresses) ? config.addresses : [],
    ipFilterMessage: config.message || "",
  };
};

/**
 * Serialize IP filter form state to JSON for server
 */
const serializeIpFilter = (formData: FormFormData): string | null => {
  // If no addresses configured, return null (no filter)
  if (!formData.ipFilterAddresses || formData.ipFilterAddresses.length === 0) {
    return null;
  }

  const config: {
    mode: "allow" | "deny";
    addresses: string[];
    message?: string;
  } = {
    mode: formData.ipFilterMode,
    addresses: formData.ipFilterAddresses,
  };

  // Only include message if not empty
  if (formData.ipFilterMessage.trim()) {
    config.message = formData.ipFilterMessage.trim();
  }

  return JSON.stringify(config);
};

export interface FormsEditModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Form to edit (if null/undefined, it's create mode) */
  form?: Form | null;

  /** Callback when form should be created */
  onCreateForm?: (formData: FormInput) => Promise<Form | null>;

  /** Callback when form should be updated */
  onUpdateForm?: (formId: string, formData: FormInput) => Promise<Form | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from form operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface FormFormData {
  // Basic
  name: string;
  path: string;
  description: string;
  status: number;
  // Email & Notifications
  subject: string;
  accountEmailId: string | null;
  sendConfirmation: boolean;
  notificationTemplateId: string | null;
  confirmationTemplateId: string | null;
  // Connectors
  connectorId: string | null;
  filesConnectorId: string | null;
  emailConnectorId: string | null;
  // Submission Settings
  submissionsQtyLimit: number | null;
  allowFiles: boolean;
  ignoreExtraFields: boolean;
  doNotStoreSubmissionData: boolean;
  gibberishCheck: string;
  spamCheck: boolean;
  dataRetentionDays: number | null;
  // Scheduling
  startsAt: string | null;
  endsAt: string | null;
  // Redirects & Security
  redirectUrl: string;
  allowedReferers: string;

  ipFilterMode: "allow" | "deny";
  ipFilterAddresses: string[];
  ipFilterMessage: string;

  // Custom Messages
  thankYouMessage: string;
  messageOnLimitReached: string;
  messageBeforeStart: string;
  messageAfterEnd: string;
  messageOnDisabled: string;
  // Advanced
  formConfiguration: string;
  workflowId: string;

  duplicateOfId?: string | null | undefined;
}

const initialFormData: FormFormData = {
  // Basic
  name: "",
  path: "",
  description: "",
  status: 100,
  // Email & Notifications
  subject: "",
  accountEmailId: null,
  sendConfirmation: false,
  notificationTemplateId: null,
  confirmationTemplateId: null,
  // Connectors
  connectorId: null,
  filesConnectorId: null,
  emailConnectorId: null,
  // Submission Settings
  submissionsQtyLimit: null,
  allowFiles: false,
  ignoreExtraFields: false,
  doNotStoreSubmissionData: false,
  gibberishCheck: "medium",
  spamCheck: false,
  dataRetentionDays: null,
  // Scheduling
  startsAt: null,
  endsAt: null,
  // Redirects & Security
  redirectUrl: "",
  allowedReferers: "",
  ipFilterMode: "deny",
  ipFilterAddresses: [],
  ipFilterMessage: "",
  // Custom Messages
  thankYouMessage: "",
  messageOnLimitReached: "",
  messageBeforeStart: "",
  messageAfterEnd: "",
  messageOnDisabled: "",
  // Advanced
  formConfiguration: "",
  workflowId: "",

  duplicateOfId: null,
};

/**
 * Parse UTC date string from server and convert to local time string
 * for display in DateTimePicker (format: 'YYYY-MM-DD HH:mm:ss')
 */
const parseUtcToLocalString = (
  utcDateStr: string | undefined | null,
): string | null => {
  if (!utcDateStr) return null;
  const parsed = dayjs.utc(utcDateStr);
  if (!parsed.isValid()) return null;
  // Convert UTC to local time and format for the picker
  return parsed.local().format("YYYY-MM-DD HH:mm:ss");
};

/**
 * Parse local time string from DateTimePicker and convert to UTC ISO string
 * for sending to server
 */
const parseLocalToUtcIso = (localDateStr: string | null): string | null => {
  if (!localDateStr) return null;
  // Parse as local time and convert to UTC ISO string
  const parsed = dayjs(localDateStr, "YYYY-MM-DD HH:mm:ss");
  if (!parsed.isValid()) return null;
  return parsed.utc().toISOString();
};

/**
 * Parse local time string to Date object for DateTimePicker value prop
 */
const localStringToDate = (localDateStr: string | null): Date | null => {
  if (!localDateStr) return null;
  const parsed = dayjs(localDateStr, "YYYY-MM-DD HH:mm:ss");
  if (!parsed.isValid()) return null;
  return parsed.toDate();
};

export const FormsEditModal: React.FC<FormsEditModalProps> = ({
  opened,
  onClose,
  form,
  onCreateForm,
  onUpdateForm,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const navigate = useNavigate();
  const { chechAppPlanFeature } = useApp();
  const { is12Hour } = useTimeFormat();
  const [formData, setFormData] = useState<FormFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<any>({});
  const [copied, setCopied] = useState(false);

  const isEditMode = Boolean(form) && Boolean(form?.id);
  const isSubmitting = isCreating || isUpdating;
  const canEditForm = isUpdating
    ? form?.rights?.includes(OPERATION_RIGHTS.UPDATE)
    : true;

  // Reset form when modal opens/closes or form changes
  useEffect(() => {
    if (opened) {
      if (form) {
        // Edit mode - populate form with form data
        // Convert UTC dates from server to local time strings
        setFormData({
          // Basic
          name: form.name || "",
          path: form.path || "",
          description: form.description || "",
          status: form.status ?? 100,
          // Email & Notifications
          subject: form.subject || "",
          accountEmailId: form.accountEmailId?.toString() || null,
          sendConfirmation: form.sendConfirmation ?? false,
          notificationTemplateId:
            form.notificationTemplateId?.toString() || null,
          confirmationTemplateId:
            form.confirmationTemplateId?.toString() || null,
          // Connectors
          connectorId: form.connectorId?.toString() || null,
          filesConnectorId: form.filesConnectorId?.toString() || null,
          emailConnectorId: form.emailConnectorId?.toString() || null,
          // Submission Settings
          submissionsQtyLimit: form.submissionsQtyLimit ?? null,
          allowFiles: form.allowFiles ?? true,
          ignoreExtraFields: form.ignoreExtraFields ?? false,
          doNotStoreSubmissionData: form.doNotStoreSubmissionData ?? false,
          gibberishCheck: form.gibberishCheck ?? "medium",
          spamCheck: form.spamCheck ?? false,
          dataRetentionDays: form.dataRetentionDays ?? null,
          // Scheduling - convert UTC from server to local time
          startsAt: parseUtcToLocalString(form.startsAt),
          endsAt: parseUtcToLocalString(form.endsAt),
          // Redirects & Security
          redirectUrl: form.redirectUrl || "",
          allowedReferers: form.allowedReferers || "",
          ...parseIpFilterForForm(form.ipFilter),
          // Custom Messages
          thankYouMessage: form.thankYouMessage || "",
          messageOnLimitReached: form.messageOnLimitReached || "",
          messageBeforeStart: form.messageBeforeStart || "",
          messageAfterEnd: form.messageAfterEnd || "",
          messageOnDisabled: form.messageOnDisabled || "",
          // Advanced
          formConfiguration: form.formConfiguration || "",
          workflowId: form.workflowId || "",
          duplicateOfId: form.duplicateOfId,
        });
      } else {
        // Create mode - use defaults
        setFormData(initialFormData);
      }

      setValidationErrors({});
      setCopied(false);
      onClearError?.();
    }
  }, [opened, form, onClearError]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: any = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = "Form name is required";
    }

    // Validate date range
    if (formData.startsAt && formData.endsAt) {
      const startDate = dayjs(formData.startsAt, "YYYY-MM-DD HH:mm:ss");
      const endDate = dayjs(formData.endsAt, "YYYY-MM-DD HH:mm:ss");
      if (
        startDate.isValid() &&
        endDate.isValid() &&
        !startDate.isBefore(endDate)
      ) {
        newErrors.endsAt = "End date must be after start date";
      }
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field changes
  const handleFieldChange = (field: keyof FormFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle date field changes from DateTimePicker (receives string 'YYYY-MM-DD HH:mm:ss')
  const handleDateChange = (
    field: "startsAt" | "endsAt",
    value: string | null,
  ) => {
    // DateTimePicker passes string in format 'YYYY-MM-DD HH:mm:ss' or null
    handleFieldChange(field, value || null);
  };

  // Handle copy Form ID
  const handleCopyFormId = async () => {
    try {
      await navigator.clipboard.writeText(formData.path);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  // Handle navigate to edit fields
  const handleEditFields = () => {
    if (form?.id) {
      navigate(`/forms/${form.id}/edit-fields`);
    }
  };

  // Handle save
  const handleSubmit = async () => {
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare form data - convert local time to UTC for server
      const formSubmitData: FormInput = {
        // Basic
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        status: formData.status,
        // Email & Notifications
        subject: formData.subject.trim() || null,
        accountEmailId: formData.accountEmailId || null,
        sendConfirmation: formData.sendConfirmation,
        notificationTemplateId: formData.notificationTemplateId || null,
        confirmationTemplateId: formData.confirmationTemplateId || null,
        // Connectors
        connectorId: formData.connectorId || null,
        filesConnectorId: formData.filesConnectorId || null,
        emailConnectorId: formData.emailConnectorId || null,
        // Submission Settings
        submissionsQtyLimit: formData.submissionsQtyLimit,
        allowFiles: formData.allowFiles,
        ignoreExtraFields: formData.ignoreExtraFields,
        doNotStoreSubmissionData: formData.doNotStoreSubmissionData,
        gibberishCheck: formData.gibberishCheck,
        spamCheck: formData.spamCheck,
        dataRetentionDays: formData.dataRetentionDays,
        // Scheduling - convert local time to UTC for server
        startsAt: parseLocalToUtcIso(formData.startsAt),
        endsAt: parseLocalToUtcIso(formData.endsAt),
        // Redirects & Security
        redirectUrl: formData.redirectUrl.trim() || null,
        allowedReferers: formData.allowedReferers.trim() || null,
        ipFilter: serializeIpFilter(formData),
        // Custom Messages
        thankYouMessage: formData.thankYouMessage.trim() || null,
        messageOnLimitReached: formData.messageOnLimitReached.trim() || null,
        messageBeforeStart: formData.messageBeforeStart.trim() || null,
        messageAfterEnd: formData.messageAfterEnd.trim() || null,
        messageOnDisabled: formData.messageOnDisabled.trim() || null,
        // Advanced
        formConfiguration: formData.formConfiguration.trim() || null,
        workflowId: formData.workflowId.trim() || null,
        duplicateOfId: formData.duplicateOfId,
      };

      if (isEditMode && form && onUpdateForm) {
        // Update existing form
        await onUpdateForm(form.id || "", formSubmitData);
      } else if (!isEditMode && onCreateForm) {
        // Create new form
        await onCreateForm(formSubmitData);
      }

      // Parent component will handle navigation and closing
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  // Get the error to display for a field
  const getDisplayError = (field: string): string | undefined => {
    return validationErrors[field];
  };

  // Get minimum date for end date picker (based on start date)
  const getMinEndDate = (): Date | undefined => {
    if (!formData.startsAt) return undefined;
    const startDate = localStringToDate(formData.startsAt);
    return startDate || undefined;
  };

  const supportsEmailConnector = chechAppPlanFeature(
    FEATURE_CODES.BYOE,
    true,
  ).didSucceed();

  const supportsConnectors = chechAppPlanFeature(
    FEATURE_CODES.MAX_CONNECTORS,
    1,
  ).didSucceed();

  const supportsIpFilter = chechAppPlanFeature(
    FEATURE_CODES.IP_FILTER,
    true,
  ).didSucceed();

  const premiumEmailConfirmation = usePremiumField({
    label: "Send confirmation to submitter",
    description:
      "Send a copy of the submission to the email address provided in the form (requires a reply-to field)",
    premiumFeatureEnabled: chechAppPlanFeature(
      FEATURE_CODES.EMAIL_CONFIRMATIONS,
      true,
    ).didSucceed(),
  });

  const premiumFileUploads = usePremiumField({
    label: "Allow file uploads",
    description: "Enable file upload fields for this form",
    premiumFeatureEnabled: chechAppPlanFeature(
      FEATURE_CODES.FILES_UPLOAD,
      true,
    ).didSucceed(),
  });

  const premiumAiSpamCheck = usePremiumField({
    label: "AI-powered spam detection",
    description:
      "Use AI to analyze submissions and automatically detect spam, phishing attempts, and promotional content",
    premiumFeatureEnabled: chechAppPlanFeature(
      FEATURE_CODES.AI_SPAM_CHECK,
      true,
    ).didSucceed(),
  });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={isEditMode ? "Edit Form" : "Create New Form"}
      position="right"
      size="lg"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={lastError}
      actionType={canEditForm ? "save-cancel" : "close"}
      onConfirm={handleSubmit}
      leftActions={
        isEditMode
          ? [
              {
                label: canEditForm ? "Edit Fields" : "View Fields",
                variant: "tertiary",
                iconName: "Settings",
                onClick: handleEditFields,
              },
            ]
          : undefined
      }
    >
      <Stack gap="md">
        {/* Basic Information - Always Visible */}
        <TextInput
          label="Form Name"
          placeholder="Enter form name"
          value={formData.name}
          onChange={(e) => handleFieldChange("name", e.target.value)}
          error={getDisplayError("name")}
          required
          withAsterisk
          size="md"
        />

        {formData.path && (
          <TextInput
            label="Form ID"
            placeholder="The ID will be generated upon form creation"
            value={formData.path}
            onChange={(e) => handleFieldChange("path", e.target.value)}
            error={getDisplayError("path")}
            readOnly
            size="md"
            rightSection={
              <Tooltip label={copied ? "Copied!" : "Copy Form ID"}>
                <ActionIcon
                  variant="subtle"
                  color={copied ? "green" : "gray"}
                  onClick={handleCopyFormId}
                  aria-label="Copy Form ID"
                >
                  {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                </ActionIcon>
              </Tooltip>
            }
          />
        )}

        <Textarea
          label="Description"
          placeholder="Enter form description (optional)"
          value={formData.description}
          onChange={(e) => handleFieldChange("description", e.target.value)}
          minRows={2}
          maxRows={4}
          autosize
          size="md"
        />

        <StatusSelector
          label="Status"
          value={formData.status}
          onChange={(value) => handleFieldChange("status", value)}
          size="md"
        />

        {/* Accordion Sections */}
        <Accordion
          variant="separated"
          radius="md"
          defaultValue={isEditMode ? null : "email"}
        >
          {/* Email & Notifications */}
          <Accordion.Item value="email">
            <Accordion.Control>
              <Text fw={500}>Email & Notifications</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <AccountEmailSelector
                  label="Send Notification Email To"
                  value={formData.accountEmailId}
                  onChange={(accountEmailId) =>
                    handleFieldChange("accountEmailId", accountEmailId)
                  }
                  placeholder="Select email for notifications"
                  confirmedOnly
                />

                <TextInput
                  label="Email Subject"
                  placeholder="Subject line for submission emails"
                  value={formData.subject}
                  onChange={(e) => handleFieldChange("subject", e.target.value)}
                  description="Overrides the subject defined in the email template"
                  size="md"
                />

                <ConnectorSelector
                  label="Email Connector"
                  value={formData.emailConnectorId}
                  onChange={(emailConnectorId) =>
                    handleFieldChange("emailConnectorId", emailConnectorId)
                  }
                  placeholder="Use FormSubmits email sender (default)"
                  description="What connector to use to send email notification and confirmations. If not selected, emails are sent using FormSubmits email capabilities."
                  types={[
                    CONNECTOR_TYPES.SENDGRID,
                    CONNECTOR_TYPES.SMTP,
                    CONNECTOR_TYPES.AWS_SES,
                  ]}
                  premiumFeatureEnabled={supportsEmailConnector}
                />

                <EmailTemplateSelector
                  label="Notification Template"
                  value={formData.notificationTemplateId}
                  onChange={(templateId) =>
                    handleFieldChange("notificationTemplateId", templateId)
                  }
                  templateType={TEMPLATE_TYPES.NOTIFICATION}
                  placeholder="Use default notification template"
                  description="Custom email template for admin notifications when submissions are received. Works only with email connector selected"
                  premiumFeatureEnabled={supportsEmailConnector}
                  disabled={!formData.emailConnectorId}
                />

                <Switch
                  label={premiumEmailConfirmation.label}
                  description={premiumEmailConfirmation.description}
                  disabled={premiumEmailConfirmation.isLocked}
                  checked={formData.sendConfirmation}
                  onChange={(e) =>
                    handleFieldChange(
                      "sendConfirmation",
                      e.currentTarget.checked,
                    )
                  }
                  size="md"
                />

                {formData.sendConfirmation && (
                  <EmailTemplateSelector
                    label="Confirmation Template"
                    value={formData.confirmationTemplateId}
                    onChange={(templateId) =>
                      handleFieldChange("confirmationTemplateId", templateId)
                    }
                    templateType={TEMPLATE_TYPES.CONFIRMATION}
                    placeholder="Use default confirmation template"
                    description="Email template for confirmation emails sent to form submitters"
                    premiumFeatureEnabled={supportsEmailConnector}
                  />
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Connectors */}
          <Accordion.Item value="connectors">
            <Accordion.Control>
              <Text fw={500}>Connectors</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <Text size="sm" c="dimmed">
                  Configure where form submission data and uploaded files are
                  sent.
                </Text>

                <ConnectorSelector
                  label="Data Connector"
                  value={formData.connectorId}
                  onChange={(connectorId) =>
                    handleFieldChange("connectorId", connectorId)
                  }
                  placeholder="Select connector for form data"
                  description="Where to send form submission data (webhook, CRM, etc.)"
                  premiumFeatureEnabled={supportsConnectors}
                />

                <ConnectorSelector
                  label="Files Connector"
                  value={formData.filesConnectorId}
                  onChange={(filesConnectorId) =>
                    handleFieldChange("filesConnectorId", filesConnectorId)
                  }
                  placeholder="Use FormSubmits storage (default)"
                  description="Where to store uploaded files. If not selected, files are stored in your FormSubmits account storage."
                  types={FILE_STORAGE_CONNECTOR_TYPES}
                  premiumFeatureEnabled={supportsConnectors}
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Submission Settings */}
          <Accordion.Item value="submissions">
            <Accordion.Control>
              <Text fw={500}>Submission Settings</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <NumberInput
                  label="Submissions Limit"
                  placeholder="Unlimited"
                  description="Maximum number of submissions allowed (leave empty for unlimited)"
                  value={formData.submissionsQtyLimit ?? ""}
                  onChange={(value) =>
                    handleFieldChange(
                      "submissionsQtyLimit",
                      value === "" ? null : value,
                    )
                  }
                  min={1}
                  size="md"
                />

                <NumberInput
                  label="Data Retention (days)"
                  placeholder="Forever"
                  description="Number of days to keep submission data (leave empty to keep as long as your current plan allows)"
                  value={formData.dataRetentionDays ?? ""}
                  onChange={(value) =>
                    handleFieldChange(
                      "dataRetentionDays",
                      value === "" ? null : value,
                    )
                  }
                  min={1}
                  size="md"
                />

                <Switch
                  label={premiumFileUploads.label}
                  description={premiumFileUploads.description}
                  disabled={premiumFileUploads.isLocked}
                  checked={formData.allowFiles}
                  onChange={(e) =>
                    handleFieldChange("allowFiles", e.currentTarget.checked)
                  }
                  size="md"
                />

                <Switch
                  label="Ignore extra fields"
                  description="Silently ignore fields not defined in the form configuration"
                  checked={formData.ignoreExtraFields}
                  onChange={(e) =>
                    handleFieldChange(
                      "ignoreExtraFields",
                      e.currentTarget.checked,
                    )
                  }
                  size="md"
                />

                <Switch
                  label="Do not store submission data"
                  description="Only track submission counts without storing the actual data (for privacy)"
                  checked={formData.doNotStoreSubmissionData}
                  onChange={(e) =>
                    handleFieldChange(
                      "doNotStoreSubmissionData",
                      e.currentTarget.checked,
                    )
                  }
                  size="md"
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Scheduling */}
          <Accordion.Item value="scheduling">
            <Accordion.Control>
              <Text fw={500}>Scheduling</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <Text size="sm" c="dimmed">
                  Set a date range when this form accepts submissions. Leave
                  empty to accept submissions anytime. Times are in your local
                  timezone.
                </Text>

                <Group grow>
                  <DateTimePicker
                    label="Starts At"
                    placeholder="No start date"
                    value={formData.startsAt}
                    onChange={(value) =>
                      handleDateChange("startsAt", value as string | null)
                    }
                    valueFormat={
                      is12Hour ? "MMM D, YYYY h:mm A" : "MMM D, YYYY HH:mm"
                    }
                    timePickerProps={{
                      withDropdown: true,
                      format: is12Hour ? "12h" : "24h",
                      popoverProps: { withinPortal: false },
                    }}
                    error={getDisplayError("startsAt")}
                    clearable
                    size="md"
                  />

                  <DateTimePicker
                    label="Ends At"
                    placeholder="No end date"
                    value={formData.endsAt}
                    onChange={(value) =>
                      handleDateChange("endsAt", value as string | null)
                    }
                    valueFormat={
                      is12Hour ? "MMM D, YYYY h:mm A" : "MMM D, YYYY HH:mm"
                    }
                    timePickerProps={{
                      withDropdown: true,
                      format: is12Hour ? "12h" : "24h",
                      popoverProps: { withinPortal: false },
                    }}
                    error={getDisplayError("endsAt")}
                    clearable
                    size="md"
                    minDate={getMinEndDate()}
                  />
                </Group>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Redirects & Security */}
          <Accordion.Item value="redirects">
            <Accordion.Control>
              <Text fw={500}>Redirects & Security</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <TextInput
                  label="Redirect URL"
                  placeholder="https://example.com/thank-you"
                  value={formData.redirectUrl}
                  onChange={(e) =>
                    handleFieldChange("redirectUrl", e.target.value)
                  }
                  description="URL to redirect after successful submission"
                  size="md"
                />

                <TagsInput
                  label="Allowed Referers"
                  placeholder="Type domain and press Enter"
                  value={parseReferers(formData.allowedReferers)}
                  onChange={(tags) =>
                    handleFieldChange(
                      "allowedReferers",
                      serializeReferers(tags),
                    )
                  }
                  description="Domains allowed to submit this form (for CORS protection)"
                  splitChars={[",", " "]} // Only comma, no space
                  allowDuplicates={false}
                  clearable
                  size="md"
                />

                <IpFilterEditor
                  value={{
                    mode: formData.ipFilterMode,
                    addresses: formData.ipFilterAddresses,
                    message: formData.ipFilterMessage,
                  }}
                  onChange={(ipFilter: IpFilterValue) => {
                    handleFieldChange("ipFilterMode", ipFilter.mode);
                    handleFieldChange("ipFilterAddresses", ipFilter.addresses);
                    handleFieldChange("ipFilterMessage", ipFilter.message);
                  }}
                  description="Restrict form submissions based on IP address"
                  premiumFeatureEnabled={supportsIpFilter}
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Spam Protection */}
          <Accordion.Item value="spamProtection">
            <Accordion.Control>
              <Text fw={500}>Spam Protection</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <Text size="sm" c="dimmed">
                  Configure automatic detection of spam and gibberish
                  submissions.
                </Text>

                <GibberishCheckSelector
                  value={formData.gibberishCheck}
                  onChange={(level) =>
                    handleFieldChange("gibberishCheck", level)
                  }
                  description="Detect random keyboard mashing, repetitive patterns, and meaningless text in short and long text fields"
                />

                {/* ADD THIS SWITCH BELOW GibberishCheckSelector */}
                <Switch
                  label={premiumAiSpamCheck.label}
                  description={premiumAiSpamCheck.description}
                  disabled={premiumAiSpamCheck.isLocked}
                  checked={formData.spamCheck}
                  onChange={(e) =>
                    handleFieldChange("spamCheck", e.currentTarget.checked)
                  }
                  size="md"
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Custom Messages */}
          <Accordion.Item value="messages">
            <Accordion.Control>
              <Text fw={500}>Custom Messages</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <Textarea
                  label="Thank You Message"
                  placeholder="Thank you for your submission!"
                  value={formData.thankYouMessage}
                  onChange={(e) =>
                    handleFieldChange("thankYouMessage", e.target.value)
                  }
                  description="Message shown after successful form submission"
                  minRows={2}
                  maxRows={4}
                  autosize
                  size="md"
                />

                <Textarea
                  label="Limit Reached Message"
                  placeholder="Sorry, this form is no longer accepting submissions."
                  value={formData.messageOnLimitReached}
                  onChange={(e) =>
                    handleFieldChange("messageOnLimitReached", e.target.value)
                  }
                  description="Message shown when submission limit is reached"
                  minRows={2}
                  maxRows={4}
                  autosize
                  size="md"
                />

                <Textarea
                  label="Before Start Message"
                  placeholder="This form is not yet accepting submissions. Please check back later."
                  value={formData.messageBeforeStart}
                  onChange={(e) =>
                    handleFieldChange("messageBeforeStart", e.target.value)
                  }
                  description="Message shown before the form start date"
                  minRows={2}
                  maxRows={4}
                  autosize
                  size="md"
                />

                <Textarea
                  label="After End Message"
                  placeholder="This form is no longer accepting submissions."
                  value={formData.messageAfterEnd}
                  onChange={(e) =>
                    handleFieldChange("messageAfterEnd", e.target.value)
                  }
                  description="Message shown after the form end date"
                  minRows={2}
                  maxRows={4}
                  autosize
                  size="md"
                />

                <Textarea
                  label="Disabled Message"
                  placeholder="This form is currently disabled."
                  value={formData.messageOnDisabled}
                  onChange={(e) =>
                    handleFieldChange("messageOnDisabled", e.target.value)
                  }
                  description="Message shown when the form is disabled"
                  minRows={2}
                  maxRows={4}
                  autosize
                  size="md"
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Advanced */}
          <Accordion.Item value="advanced">
            <Accordion.Control>
              <Text fw={500}>Advanced</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <WorkflowSelector
                  name="workflowId"
                  label="Workflow"
                  description="Workflow to execute when a submission is received"
                  placeholder="No workflow (default)"
                  value={formData.workflowId || null}
                  onChange={({ value }) =>
                    handleFieldChange("workflowId", value ?? "")
                  }
                  size="md"
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Stack>
    </AppDrawer>
  );
};
